// Import dynamique pour éviter de crasher l'app au chargement
let pdfjsLib = null;

async function getPdfjs() {
  if (pdfjsLib) return pdfjsLib;
  pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
  // Configurer le worker avec le fichier copié par webpack
  const workerUrl = new URL(
    'pdfjs-dist/legacy/build/pdf.worker.mjs',
    import.meta.url
  );
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl.toString();
  return pdfjsLib;
}

/**
 * Parse un montant français : "1 855,90" → 1855.90, "493,13-" → -493.13
 */
function parseMontant(str) {
  if (!str || typeof str !== 'string') return null;
  const cleaned = str.replace(/\s/g, '').replace(/\u00a0/g, '');
  const isNeg = cleaned.endsWith('-');
  const num = parseFloat(cleaned.replace(/-$/, '').replace(',', '.'));
  if (isNaN(num)) return null;
  return isNeg ? -num : num;
}

/**
 * Groupe les items texte par ligne (proximité Y ± 3px)
 * Retourne un tableau de lignes, chaque ligne = tableau d'items triés par X
 */
function groupItemsIntoLines(items) {
  if (!items.length) return [];

  // Trier par Y descendant (haut de page en premier)
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);

  const lines = [];
  let currentLine = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i];
    const lastY = currentLine[0].y;
    if (Math.abs(item.y - lastY) <= 3) {
      currentLine.push(item);
    } else {
      currentLine.sort((a, b) => a.x - b.x);
      lines.push(currentLine);
      currentLine = [item];
    }
  }
  if (currentLine.length) {
    currentLine.sort((a, b) => a.x - b.x);
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Extrait le texte concaténé d'une ligne
 */
function lineText(line) {
  return line.map(it => it.str).join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Cherche un N° Salarié dans les items (format XXXX.XXXXX)
 */
function findNumeroSalarie(items) {
  for (const item of items) {
    const match = item.str.match(/^\d{4}\.\d{4,5}$/);
    if (match) return match[0];
  }
  return null;
}

/**
 * Cherche le nom et prénom du salarié.
 * Format attendu : "Mme NOM Prénom" ou "M. NOM Prénom" ou "Mr NOM Prénom"
 * Le nom est en MAJUSCULES, le prénom en minuscules/mixte.
 */
function findNomPrenom(items) {
  for (const item of items) {
    // Pattern : civilité optionnelle + NOM (majuscules) + Prénom (mixte)
    const match = item.str.match(/^(?:Mme|Mr?\.?)\s+([A-ZÉÈÊËÀÂÄÔÙÛÜÏÎÇÑ][A-ZÉÈÊËÀÂÄÔÙÛÜÏÎÇÑ\s-]+?)\s+([A-ZÉÈÊËÀÂÄÔÙÛÜÏÎÇÑ][a-zéèêëàâäôùûüïîçñ][\w\s-éèêëàâäôùûüïîçñ]*)$/);
    if (match) {
      return { nom: match[1].trim(), prenom: match[2].trim() };
    }
  }
  // Fallback : chercher sans civilité — NOM en majuscules suivi de prénom mixte
  for (const item of items) {
    const match = item.str.match(/^([A-ZÉÈÊËÀÂÄÔÙÛÜÏÎÇÑ]{2,}(?:\s+[A-ZÉÈÊËÀÂÄÔÙÛÜÏÎÇÑ]{2,})*)\s+([A-ZÉÈÊËÀÂÄÔÙÛÜÏÎÇÑ][a-zéèêëàâäôùûüïîçñ][\w\s-éèêëàâäôùûüïîçñ]*)$/);
    if (match) {
      const nom = match[1].trim();
      // Exclure les faux positifs (mots-clés connus)
      if (['ELEMENTS DE', 'COTISATIONS', 'AUTRES', 'SALAIRE', 'BULLETIN DE', 'CODE DU', 'NET', 'TOTAL'].some(kw => nom.startsWith(kw))) continue;
      return { nom, prenom: match[2].trim() };
    }
  }
  return null;
}

/**
 * Cherche la période de paie (format JJ/MM/AA - JJ/MM/AA)
 */
function findPeriode(lines) {
  for (const line of lines) {
    const text = lineText(line);
    const match = text.match(/(\d{2}\/\d{2}\/\d{2})\s*-\s*(\d{2}\/\d{2}\/\d{2})/);
    if (match) return `${match[1]} - ${match[2]}`;
  }
  return null;
}

/**
 * Cherche l'indicateur de page (ex: "1/1", "1/2", "2/2")
 */
function findPageIndicator(lines) {
  // Chercher en partant du bas de page
  for (let i = lines.length - 1; i >= Math.max(0, lines.length - 5); i--) {
    const text = lineText(lines[i]);
    const match = text.match(/(\d)\/(\d)/);
    if (match) return { current: parseInt(match[1]), total: parseInt(match[2]) };
  }
  return null;
}

/**
 * Cherche le nom de la société (première ligne du PDF, en haut à gauche)
 */
function findSociete(lines) {
  // La société est la toute première ligne du PDF, en haut à gauche (x < 250)
  // On filtre uniquement les items gauches pour éviter le titre "BULLETIN DE SALAIRE" à droite
  for (const line of lines) {
    const leftItems = line.filter(it => it.x < 250);
    if (leftItems.length === 0) continue;
    const t = leftItems.map(it => it.str).join(' ').trim();
    if (!t || t.length < 3) continue;
    if (t.match(/^(SIRET|NAF|N°|BULLETIN|\d{2}\/\d{2})/i)) continue;
    return t;
  }
  return null;
}

/**
 * Cherche la qualification dans le bloc info salarié
 */
function findQualification(lines) {
  // Filtrer uniquement les items gauches (x < 250) pour éviter l'adresse du salarié à droite
  for (const line of lines) {
    const leftItems = line.filter(it => it.x < 250);
    if (leftItems.length === 0) continue;
    const text = leftItems.map(it => it.str).join(' ').replace(/\s+/g, ' ').trim();
    const match = text.match(/Qualification\s+(.+)/i);
    if (match) return match[1].trim();
  }
  return null;
}

/**
 * Cherche la date d'ancienneté (format JJ/MM/AA ou JJ/MM/AAAA)
 */
function findDateAnciennete(lines) {
  for (const line of lines) {
    const text = lineText(line);
    const match = text.match(/Dt\s*anciennet[eé]\s+(\d{2}\/\d{2}\/\d{2,4})/i);
    if (match) {
      const parts = match[1].split('/');
      let year = parseInt(parts[2]);
      // Convertir année 2 chiffres → 4 chiffres
      if (year < 100) year = year > 50 ? 1900 + year : 2000 + year;
      return `${year}-${parts[1]}-${parts[0]}`; // format ISO
    }
  }
  return null;
}

/**
 * Parse les sections d'une page de bulletin
 */
function parseSections(lines) {
  const result = {
    lignes: [],
    salaire_brut: null,
    cotisations_salariales: null,
    cotisations_patronales: null,
    total_verse: null,
  };

  let currentSection = null; // 'brut', 'cotisations', 'non_soumis'

  for (let i = 0; i < lines.length; i++) {
    const text = lineText(lines[i]);

    // Détecter les sections
    if (text.match(/ELEMENTS DE REVENU BRUT/i)) {
      currentSection = 'brut';
      continue;
    }
    if (text.match(/COTISATIONS SOCIALES/i)) {
      currentSection = 'cotisations';
      continue;
    }
    if (text.match(/AUTRES ELEMENTS NON SOUMIS/i)) {
      currentSection = 'non_soumis';
      continue;
    }

    // Fin de section non_soumis : Net social
    if (text.match(/^Net social/i)) {
      currentSection = null;
      continue;
    }

    // SALAIRE BRUT total
    if (text.match(/SALAIRE BRUT\s*:/i)) {
      const amounts = lines[i].filter(it => parseMontant(it.str) !== null);
      if (amounts.length > 0) {
        // Prendre le dernier montant sur cette ligne (c'est le total)
        const lastAmount = parseMontant(amounts[amounts.length - 1].str);
        if (lastAmount !== null && lastAmount > 100) {
          result.salaire_brut = lastAmount;
        }
      }
      currentSection = 'cotisations';
      continue;
    }

    // TOTAL COTISATIONS
    if (text.match(/TOTAL COTISATIONS\s*:/i)) {
      // Les montants sur cette ligne : salariales puis patronales
      const amounts = lines[i]
        .map(it => parseMontant(it.str))
        .filter(m => m !== null && Math.abs(m) > 10);
      if (amounts.length >= 2) {
        result.cotisations_salariales = Math.abs(amounts[0]);
        result.cotisations_patronales = Math.abs(amounts[1]);
      } else if (amounts.length === 1) {
        result.cotisations_salariales = Math.abs(amounts[0]);
      }
      currentSection = null;
      continue;
    }

    // Total versé par l'employeur
    if (text.match(/Total vers.* par l.employeur/i)) {
      // Le montant peut être sur cette ligne ou la suivante
      const amounts = lines[i]
        .map(it => parseMontant(it.str))
        .filter(m => m !== null && m > 100);
      if (amounts.length > 0) {
        result.total_verse = amounts[amounts.length - 1];
      } else if (i + 1 < lines.length) {
        const nextAmounts = lines[i + 1]
          .map(it => parseMontant(it.str))
          .filter(m => m !== null && m > 100);
        if (nextAmounts.length > 0) {
          result.total_verse = nextAmounts[nextAmounts.length - 1];
        }
      }
      continue;
    }

    // Parser les lignes de détail (code 4 chiffres + libellé + montant)
    if (currentSection === 'brut' || currentSection === 'non_soumis') {
      // Une ligne de détail commence par un code 4 chiffres
      const firstItem = lines[i][0];
      if (firstItem && /^\d{4}$/.test(firstItem.str.trim())) {
        const code = firstItem.str.trim();

        // Extraire le libellé (items texte après le code, avant les chiffres)
        let libelle = '';
        const montants = [];

        for (let j = 1; j < lines[i].length; j++) {
          const item = lines[i][j];
          const val = parseMontant(item.str);
          if (val !== null) {
            montants.push(val);
          } else if (item.str.trim() && montants.length === 0) {
            libelle += (libelle ? ' ' : '') + item.str.trim();
          }
        }

        if (libelle) {
          // Pour les éléments brut : prendre le montant de la colonne "Montant" (généralement le dernier montant positif significatif)
          // Pour les éléments non soumis : le montant peut être dans la colonne part salarié
          let montant = 0;
          if (currentSection === 'brut') {
            // Colonne Montant est la 3ème colonne numérique typiquement, ou le dernier montant
            // On prend le montant le plus à droite avant la colonne "Part employeur"
            // Basé sur les positions : Base ~300, Taux ~360, Montant ~425, Part employeur ~550
            const montantItems = lines[i]
              .filter(it => parseMontant(it.str) !== null && it.x >= 400 && it.x < 500);
            if (montantItems.length > 0) {
              montant = parseMontant(montantItems[0].str);
            } else if (montants.length > 0) {
              // Fallback : dernier montant
              montant = montants[montants.length - 1];
            }
          } else {
            // Non soumis : prendre le montant dans la zone Part employeur (x >= 500)
            // car c'est le coût total pour l'employeur
            const empItems = lines[i]
              .filter(it => parseMontant(it.str) !== null && it.x >= 500);
            if (empItems.length > 0) {
              montant = parseMontant(empItems[0].str);
            } else {
              // Fallback : montant dans la zone Montant (salarié)
              const salItems = lines[i]
                .filter(it => parseMontant(it.str) !== null && it.x >= 400 && it.x < 500);
              if (salItems.length > 0) {
                montant = parseMontant(salItems[0].str);
              } else if (montants.length > 0) {
                montant = montants[montants.length - 1];
              }
            }
          }

          if (montant !== null && montant !== 0) {
            result.lignes.push({
              code,
              libelle,
              montant,
              section: currentSection === 'brut' ? 'brut' : 'non_soumis',
            });
          }
        }
      }
    }
  }

  return result;
}

/**
 * Parse un fichier PDF de bulletins de paie.
 * Retourne un tableau de bulletins extraits.
 *
 * @param {File} file - Le fichier PDF uploadé
 * @returns {Promise<Array>} Tableau de bulletins parsés
 */
export async function parseBulletinPdf(file) {
  const pdfjs = await getPdfjs();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;

  const bulletins = [];
  let currentBulletin = null;

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();

    // Extraire les items avec positions
    const items = content.items
      .filter(it => it.str.trim())
      .map(it => ({
        str: it.str.trim(),
        x: Math.round(it.transform[4]),
        y: Math.round(it.transform[5]),
      }));

    const lines = groupItemsIntoLines(items);
    const numero = findNumeroSalarie(items);
    const periode = findPeriode(lines);
    const pageIndicator = findPageIndicator(lines);
    const nomPrenom = findNomPrenom(items);

    if (numero) {
      // Nouvelle fiche salarié
      if (currentBulletin) {
        bulletins.push(currentBulletin);
      }
      currentBulletin = {
        numero_salarie: numero,
        periode: periode || '',
        nom: nomPrenom?.nom || null,
        prenom: nomPrenom?.prenom || null,
        societe: findSociete(lines) || null,
        qualification: findQualification(lines) || null,
        date_anciennete: findDateAnciennete(lines) || null,
        salaire_brut: null,
        cotisations_salariales: null,
        cotisations_patronales: null,
        total_verse: null,
        lignes: [],
      };
    }

    // Si pas de numéro et pas de bulletin en cours, ignorer
    if (!currentBulletin) continue;

    // Parser les sections de cette page
    const sections = parseSections(lines);

    // Fusionner avec le bulletin en cours
    currentBulletin.lignes.push(...sections.lignes);
    if (sections.salaire_brut !== null) currentBulletin.salaire_brut = sections.salaire_brut;
    if (sections.cotisations_salariales !== null) currentBulletin.cotisations_salariales = sections.cotisations_salariales;
    if (sections.cotisations_patronales !== null) currentBulletin.cotisations_patronales = sections.cotisations_patronales;
    if (sections.total_verse !== null) currentBulletin.total_verse = sections.total_verse;
    if (periode && !currentBulletin.periode) currentBulletin.periode = periode;
  }

  // Ajouter le dernier bulletin
  if (currentBulletin) {
    bulletins.push(currentBulletin);
  }

  return bulletins;
}
